import { Metadata } from './Metadata';
import { clientConfiguration, scope, sessionCheckScope } from './Global';
import { OAuthState } from './OAuthState';
import { API, HTTPMethod, ContentType } from '../api/APICentral';

import { storage, TOKEN_INFO, TOKEN_EXPIRATION, PROFILE_INFO } from '../app/Storage';

import { verify, decode } from 'jsonwebtoken';

export class Authentication {
    metadata;
    tokenInfo;
    tokenExpiration;
    profileInfo;

    constructor(clientConfiguration) {
        this.metadata = new Metadata(clientConfiguration);
        this.tokenInfo = {};
        this.tokenExpiration = '';
        this.profileInfo = {};
    }

    get isLoggedIn() {
        return Object.entries(this.tokenInfo).length > 0
            && Object.entries(this.profileInfo).length > 0
            && this.tokenExpiration !== '';
    }

    get isTokenExpired() {
        if (this.tokenExpiration === '') {
            return true;
        }
        let now = new Date();
        return this.tokenExpiration.getTime() < now.getTime();
    }

    get accessToken() {
        return this.tokenInfo.access_token;
    }

    get shouldCheckSession() {
        let idToken = decode(this.tokenInfo.id_token);
        return new Date(idToken.exp * 1000) < new Date();
    }

    get username() {
        return this.profileInfo.preferred_username;
    }

    get signInUrl() {
        var params = new URLSearchParams(OAuthState.getStateAsQueryString());
        params.append("client_id", this.metadata.client_id);
        params.append("redirect_uri", this.metadata.redirect_uri);
        params.append("response_type", this.metadata.defaultResponseType);
        params.append("scope", scope);

        return this.metadata.authorizationEndpoint + "?" + params.toString();
    }

    get logoutUrl() {
        var params = new URLSearchParams();
        params.append("id_token_hint", this.tokenInfo.id_token);
        params.append("post_logout_redirect_uri", this.metadata.post_logout_redirect_uri);

        return this.metadata.signoutEndpoint + "?" + params.toString();
    }

    get manageAccontUrl() {
        return this.metadata.manage_account_redirect_uri;
    }

    get sessionCheckUrl() {
        var params = new URLSearchParams(OAuthState.getStateAsQueryString());
        params.append("client_id", this.metadata.client_id);
        params.append("redirect_uri", this.metadata.redirect_uri);
        params.append("response_type", this.metadata.defaultResponseType);
        params.append("scope", sessionCheckScope);
        params.append("id_token_hint", this.tokenInfo.id_token);
        params.append("prompt", "none");

        return this.metadata.authorizationEndpoint + "?" + params.toString();
    }

    loadData() {
        let tokenInfo = storage.get(TOKEN_INFO);
        let profileInfo = storage.get(PROFILE_INFO);
        let tokenInfoObject = {};
        let profileInfoObject = {};

        if (typeof tokenInfo !== 'undefined' && typeof profileInfo !== 'undefined') {
            var json = JSON.parse(tokenInfo);
            this.tokenInfo = Object.assign(tokenInfoObject, json);

            json = JSON.parse(profileInfo);
            this.profileInfo = Object.assign(profileInfoObject, json);
        }

        let tokenExpiration = storage.get(TOKEN_EXPIRATION);
        if (typeof tokenExpiration !== 'undefined') {
            this.tokenExpiration = new Date(tokenExpiration);
        }

        if (!this.isTokenExpired) {
            this.setAutomaticTokenRefresh();
        }

        return this.metadata.loadOpenIdMetadata().then((res) => {
            return Promise.resolve(res);
        });
    }

    parseUrl(url) {
        let parsedUrl = new URL(url);
        let receivedState = parsedUrl.searchParams.get("state");
        let authorizationCode = parsedUrl.searchParams.get("code");

        if (receivedState && OAuthState.isValidState(receivedState) && authorizationCode !== null) {
            return Promise.resolve(authorizationCode);
        }
        return Promise.reject(new Error("Ocorreu um erro durante a autenticação!"));
    }

    getToken(tokenRequestOptions) {
        var params = new URLSearchParams();

        if (tokenRequestOptions.authorization_code) {
            params.append("code", tokenRequestOptions.authorization_code);
            params.append("grant_type", "authorization_code");
            params.append("redirect_uri", this.metadata.redirect_uri);
        } else if (tokenRequestOptions.refresh_token) {
            params.append("refresh_token", tokenRequestOptions.refresh_token);
            params.append("grant_type", "refresh_token");
        } else {
            throw new Error("No refresh_token or authorization_code submitted.");
        }
        params.append("client_id", this.metadata.client_id);


        return API.authenticatedRequest({
            url: this.metadata.tokenEndpoint,
            method: HTTPMethod.POST,
            body: decodeURI(params.toString())
            /*}).then((response: Response) => {
                return response.json();
            }).then((json: JSON) => {
                let keys = {} as TokenResponse;
                let tokenResponse = Object.assign(keys, json) as TokenResponse;*/
        }).then(response => {
            let tokenResponse = JSON.parse(response);

            if (!this.validateTokenResponse(tokenResponse)) {
                throw new Error("Invalid token received.");
            }

            if (tokenResponse.scope === sessionCheckScope) {
                this.tokenInfo.id_token = tokenResponse.id_token;
                storage.set(TOKEN_INFO, JSON.stringify(this.tokenInfo));
            }
            else {
                this.tokenInfo = tokenResponse;
                storage.set(TOKEN_INFO, JSON.stringify(tokenResponse));

                let tokenExpiration = new Date();
                // expires_in is in seconds, needs to be multiplied by 1000
                tokenExpiration.setTime(tokenExpiration.getTime() + tokenResponse.expires_in * 1000);
                this.tokenExpiration = tokenExpiration;
                storage.set(TOKEN_EXPIRATION, tokenExpiration.toString());
                this.setAutomaticTokenRefresh();
            }
        }).catch((error) => {
            // Could not authenticate 
            // Should erase all login information and start over
            storage.remove(TOKEN_INFO);
            storage.remove(TOKEN_EXPIRATION);
            storage.remove(PROFILE_INFO);

            console.error(error.message);
        });
    }

    setAutomaticTokenRefresh() {
        let now = new Date();
        let milisecondsToExpire = this.tokenExpiration.getTime() - now.getTime();

        if (milisecondsToExpire > 10000) {
            setTimeout(this.refreshToken.bind(this), milisecondsToExpire - 10000);
        } else {
            setTimeout(this.refreshToken.bind(this), 0);
        }
    }

    refreshToken() {
        let refreshToken = this.tokenInfo.refresh_token;
        return this.getToken({
            refresh_token: refreshToken
        }).then((res) => {
            // New access and id_token, refresh profile info as well
            return authenticationCentral.getProfileInfo();
        });
    }

    getProfileInfo() {
        return API.authenticatedRequest({
            url: this.metadata.userInfoEndpoint,
            method: HTTPMethod.POST,
            contentType: ContentType.FORM,
            token: this.tokenInfo.access_token
        }).then(response => {
            let profileInfo = JSON.parse(response);

            this.profileInfo = profileInfo;
            storage.set(PROFILE_INFO, JSON.stringify(profileInfo));
        }).catch((error) => {
            throw new Error("Can't retrieve profile information");
        });
    }

    validateTokenResponse(response) {
        // TokenType must be Bearer
        if (response.token_type !== "Bearer") {
            throw new Error("Token type not Bearer");
        }

        // Validate id_token
        verify(response.id_token, this.metadata.signingCertificate, {
            clockTolerance: 600
        });

        // Valide scopes
        if (response.scope !== scope && response.scope !== sessionCheckScope) {
            throw new Error("Scopes differ from requested");
        }

        return true;
    }
}

export const authenticationCentral = new Authentication(clientConfiguration);


// WEBPACK FOOTER //
// ./src/authentication/Authentication.js